import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { ServiceModuleType } from '@/api/model/serviceModuleType'
import { PermissionEntityType } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import { ServiceModuleGrid } from './grid-components/ServiceModuleGrid'
import { CreateServiceModuleDialog } from './components/CreateServiceModuleDialog'
import { EditServiceModule } from './grid-components/EditServiceModule'
import { OpenServiceModule } from './grid-components/OpenServiceModule'
import type { ServiceModuleModel } from '@/api/model'
import { Plus, Search } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { IMPLEMENTED_PAGE_TYPES } from './grid-components/OpenServiceModule'
import {
  useGetApiCompaniesCurrentServicemodules,
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  usePatchApiCompaniesCurrentServicemodulesOrder,
} from '@/api/generated/company-management-user/company-management-user'
import { useQueryClient } from '@tanstack/react-query'
import { notify } from '@/lib/notify'
import {MoveServiceModuleDialog} from "./move-components/MoveServiceModuleDialog.tsx";

/** Breadcrumb-Crumb, wie ihn `AppBreadcrumb` aus `location.state` liest. */
type Crumb = { label: string; path?: string }

const folderPath = (id: number) => `/service-modules?folder=${id}`

export default function ServiceModulesPage() {
  const { t } = useTranslation(['service-modules', 'filters'])
  const { userHasPermission } = usePermissions()
  const canCreate = userHasPermission(PermissionEntityType.ServiceModules, 'Create')
  const canReorder = userHasPermission(PermissionEntityType.ServiceModules, 'Edit')
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [search, setSearch] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingModule, setEditingModule] = useState<ServiceModuleModel | null>(null)
  const [openingModule, setOpeningModule] = useState<ServiceModuleModel | null>(null)
  const [movingModule, setMovingModule] = useState<ServiceModuleModel | null>(null)

  const { data, isLoading, isError } = useGetApiCompaniesCurrentServicemodules()

  // Persist the order of the current level; afterwards reload the list so the
  // server-side `order` values are the source of truth again.
  const { mutate: reorderModules } = usePatchApiCompaniesCurrentServicemodulesOrder({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() }),
      onError: (error) => notify.apiError(error, t('toasts.reorderError')),
    },
  })

  const modules: ServiceModuleModel[] = useMemo(() => {
    const raw = (data as any)?.data ?? data
    const items: any[] = Array.isArray(raw) ? raw : raw?.items ?? []
    return items
      .filter((m) => !m.isDeleted && m.type !== ServiceModuleType.Undefined)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }, [data])

  const folderIdParam = searchParams.get('folder')
  const currentFolderId = folderIdParam ? Number(folderIdParam) : null

  // Is an "own page" module open? (URL-driven like the folder navigation,
  // so Back/breadcrumb heal themselves.)
  const openIdParam = searchParams.get('open')
  const openId = openIdParam ? Number(openIdParam) : null
  const openModule = useMemo(
    () => (openId != null ? modules.find((m) => m.id === openId) ?? null : null),
    [modules, openId],
  )
  const isPageOpen = !!openModule && IMPLEMENTED_PAGE_TYPES.has(openModule.type as ServiceModuleType)

  // Only the items of the current level (root = parent null, otherwise = this folder).
  const visibleModules = useMemo(
    () => modules.filter((m) => (m.parentFolderServiceModuleId ?? null) === currentFolderId),
    [modules, currentFolderId],
  )

  // Client-side name search within the current level (analogous to action-modules).
  const query = search.trim().toLowerCase()
  const filteredModules = useMemo(
    () => (query ? visibleModules.filter((m) => (m.name ?? '').toLowerCase().includes(query)) : visibleModules),
    [visibleModules, query],
  )

  // Folder chain (upwards) of a level → breadcrumb crumbs (like a file explorer).
  const crumbs = useMemo<Crumb[]>(() => {
    const byId = new Map(modules.map((m) => [m.id, m]))
    const chainFrom = (startId: number | null): Crumb[] => {
      const chain: Crumb[] = []
      let id: number | null = startId
      const guard = new Set<number>()
      while (id != null && !guard.has(id)) {
        guard.add(id)
        const folder = byId.get(id)
        if (!folder) break
        chain.unshift({ label: folder.name || t('crumbs.folderFallback'), path: folderPath(folder.id) })
        id = folder.parentFolderServiceModuleId ?? null
      }
      return chain
    }

    // With an open page: folder path down to the module + the module itself as a leaf (no link).
    if (isPageOpen && openModule) {
      return [
        ...chainFrom(openModule.parentFolderServiceModuleId ?? null),
        { label: openModule.name || t('crumbs.moduleFallback') },
      ]
    }
    return chainFrom(currentFolderId)
  }, [modules, currentFolderId, isPageOpen, openModule, t])

  // Mirror the path into the app breadcrumb (top, with Home→Dashboard). The source
  // of truth is the URL — so the path heals itself on breadcrumb click / Back,
  // no matter how you got here.
  useEffect(() => {
    const current = (location.state as any)?.breadcrumbs as Crumb[] | undefined
    if (JSON.stringify(current ?? []) === JSON.stringify(crumbs)) return
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...(location.state as object), breadcrumbs: crumbs },
    })
  }, [crumbs, location.pathname, location.search, location.state, navigate])

  function handleOpen(module: ServiceModuleModel) {
    if (module.type === ServiceModuleType.Folder) {
      setSearchParams({ folder: String(module.id) })
    } else if (IMPLEMENTED_PAGE_TYPES.has(module.type as ServiceModuleType)) {
      // Folder param drops away — the folder path is reconstructed from the module parent.
      setSearchParams({ open: String(module.id) })
    } else {
      setOpeningModule(module)
    }
  }

  // "Own page" types (e.g. PersonalDocuments) replace the grid entirely and are
  // rendered URL-driven (`?open`) — dispatched via OpenServiceModule.
  if (isPageOpen && openModule) {
    return (
      <OpenServiceModule
        module={openModule}
        onClose={() => setSearchParams({})}
      />
    )
  }

  return (
    <>
      <PageLayout scrollEnabled>
        <PageHeader title={t('header.title')} description={t('header.description')}>
          <PageHeaderActions>
            <Button className="h-9 gap-2" onClick={() => setIsCreateOpen(true)} disabled={!canCreate}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">{t('header.createModule')}</span>
            </Button>
          </PageHeaderActions>
        </PageHeader>

        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder={t('filters:searchbar.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
          />
        </div>

        <ServiceModuleGrid
          modules={filteredModules}
          isLoading={isLoading}
          isError={isError}
          inFolder={currentFolderId != null}
          isFiltered={!!query}
          onEdit={setEditingModule}
          onOpen={handleOpen}
          // D&D only with edit permission and without an active search (a filtered
          // subset couldn't be meaningfully reordered).
          onReorder={canReorder && !query ? (ids) => reorderModules({ data: ids }) : undefined}
          onMove={setMovingModule}
        />
      </PageLayout>

      <CreateServiceModuleDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        parentFolderServiceModuleId={currentFolderId}
        onCreated={setEditingModule}
      />

      {editingModule && (
        <EditServiceModule
          module={editingModule}
          onClose={() => setEditingModule(null)}
          modules={modules}
        />
      )}

      {openingModule && (
        <OpenServiceModule
          module={openingModule}
          onClose={() => setOpeningModule(null)}
        />
      )}
      {movingModule && (
          <MoveServiceModuleDialog
              module={movingModule}
              modules={modules}
              onClose={() => setMovingModule(null)}
          />
      )}
    </>
  )
}
